import { axios } from '@vyce/core/src/api';

import {
  AddEmployeeToPayScheduleRequest,
  Cis300SubmitsRequest,
  CISTimesheetRequest,
  CreateOrUpdateCompanyBankAccountRequest,
  DownloadCISReportRequest,
  DownloadHealthAndSafetyPDFRequest,
  EmployeesVATRequestProps,
  EmployeeVATDTO,
  GenerateNextPayrun,
  GetAgreementRequest,
  GetAgreementsRequest,
  GetAgreementVersionsRequest,
  HideAgreementVersionRequest,
  MakeAgreementVersionCurrentRequest,
  RefreshPayrolleeDocRequest,
  ReissueAgreementRequest,
  UpdateAgreementVersionNoteRequest,
  GetCompanyBankAccountRequest,
  GetEmployeeTimesheetsRequest,
  GetPayrun,
  GetPayslipsRequest,
  GetTimesheetData,
  ListEmployeeRequest,
  ListPaySchedulesRequest,
  MarkAsPaidRequest,
  PaginatedList,
  RefreshCISReportRequest,
  RegisterPayCompanyData,
  RemoveEmployeeFromModuleRequest,
  RemoveEmployeeRequest,
  SendBulkPayStatements,
  SendCISReportRequest,
  SendPayslipsRequest,
  TimesheetByStaffDTO,
} from '../types';
import {
  AgreementVersion,
  Employee,
  HMRCSetting,
  PaySchedule,
  PayrollPayload,
  PayslipsSendStarted,
  PresignedFile,
  RefreshedPayrolleeDoc,
  ReportPayload,
  TimesheetLine,
} from '../../types';
import { LookupEmployees } from '../pay';
import { GRID_PAGE_SIZE } from '../../constants';
import config from '../config';

const URL = `${config.gatewayApiUrl}`;

export const addEmployeeToPayScheduleRequest = (payload: AddEmployeeToPayScheduleRequest) => {
  return axios.post(
    `${URL}/admin/v1/pay/pay_schedules/companies/${payload.companyId}/pay_schedules/${payload.payScheduleId}/employees/${payload.employeeId}`,
    {
      pay_scheme: payload.payScheme,
      notify_employee: payload.notifyEmployee,
      notify_employer: payload.notifyEmployer,
    }
  );
};

export const createEmployeeRequest = (userId: string, employee: Partial<Employee>) => {
  return axios.post(`${URL}/admin/v1/pay/employees/users/${userId}`, employee);
};

export const updateEmployeeRequest = (employee: Partial<Employee>, employeeId: string) => {
  return axios.patch(`${URL}/admin/v1/pay/employees/${employeeId}`, employee);
};

export const createPayScheduleRequest = (
  token: string,
  companyId: string,
  data: any,
  outsourced: boolean
) => {
  return axios.post(
    `${URL}/admin/v1/pay/pay_schedules/companies/${companyId}?outsourced=${outsourced}`,
    data
  );
};

export const setPayScheduleManagersRequest = ({
  companyId,
  payScheduleId,
  data,
}: {
  companyId: string;
  payScheduleId: string;
  data: {
    to_add: string[];
    to_remove: string[];
    role_system_name: string;
  };
}) => {
  return axios.post(
    `${URL}/admin/v1/pay/pay_schedules/companies/${companyId}/pay_schedules/${payScheduleId}/managers`,
    data
  );
};

export const getPaySchedulesRequest = ({
  companyId,
  substring,
  offset = 0,
  limit = GRID_PAGE_SIZE,
}: ListPaySchedulesRequest) => {
  let query = `?limit=${limit}&offset=${offset}`;
  if (companyId) {
    query += `&company_id=${companyId}`;
  }
  if (substring) {
    query += `&substring=${substring}`;
  }
  return axios.get(`${URL}/admin/v1/pay/pay_schedules${query}`);
};

export const addEmployeeToPaySchedulesRequestByLegend = ({
  companyId,
  payScheduleId,
  employeeId,
  payScheme,
  notifyEmployee,
  notifyEmployer,
}: AddEmployeeToPayScheduleRequest) => {
  return axios.post(
    `${URL}/admin/v1/pay/pay_schedules/companies/${companyId}/pay_schedules/${payScheduleId}/employees/${employeeId}`,
    { pay_scheme: payScheme, notify_employee: notifyEmployee, notify_employer: notifyEmployer }
  );
};

export const removeEmployeeFromPayScheduleRequest = ({
  companyId,
  payScheduleId,
  userId,
}: RemoveEmployeeRequest) => {
  return axios.post(
    `${URL}/admin/v1/pay/pay_schedules/companies/${companyId}/pay_schedules/${payScheduleId}/users/remove`,
    { user_ids: [userId] }
  );
};

export const removeEmployeeFromModuleRequest = ({
  token,
  companyId,
  userId,
}: RemoveEmployeeFromModuleRequest) => {
  return axios.delete(`${URL}/admin/v1/pay/employees/companies/${companyId}/users/${userId}/pay_schedules`);
};

export const companyRegistrationRequest = (
  token: string,
  companyId: string,
  data: RegisterPayCompanyData
) => {
  return axios.post(`${URL}/admin/v1/pay/companies/${companyId}`, data);
};

export const getPayrunRequest = ({ companyId, payrunId, data }: GetPayrun) => {
  return axios.post(`${URL}/admin/v1/pay/companies/${companyId}/pay_runs/${payrunId}`, data);
};

// Replace with new API

export const getTimesheetsRequest = (payload: PayrollPayload) => {
  return axios.post(`/admin/pay/companies/timesheets/search`, payload);
};

export const getPayrunsRequest = (payload: PayrollPayload) => {
  return axios.post(`/admin/pay/companies/pay_runs/search`, payload);
};

export const getTimesheetRequest = (companyId: string, payrunId: string, data: GetTimesheetData) => {
  return axios.post<TimesheetByStaffDTO>(`/admin/pay/company/${companyId}/timesheets/${payrunId}`, data);
};

export const importTimesheetRequest = ({
  token,
  companyId,
  payrunId,
  payScheduleId,
  file,
  mapping,
}: CISTimesheetRequest) => {
  const bodyFormData = new FormData();
  bodyFormData.append('file', file);
  if (mapping) {
    bodyFormData.append('mapping', JSON.stringify(mapping));
  }
  return axios.post(
    `/admin/pay/companies/${companyId}/pay_schedules/${payScheduleId}/pay_runs/${payrunId}/timesheets`,
    bodyFormData
  );
};

export const submitTimesheetRequest = (
  companyId: string,
  payScheduleId: string,
  payrunId: string,
  data: GetTimesheetData
) => {
  return axios.post(
    `/admin/pay/companies/${companyId}/pay_schedules/${payScheduleId}/timesheets/${payrunId}/submit`,
    data
  );
};

export const reopenTimesheetRequest = (
  token: string,
  companyId: string,
  payScheduleId: string,
  payrunId: string
) => {
  return axios.post(
    `/admin/pay/company/${companyId}/pay_schedule/${payScheduleId}/timesheets/${payrunId}/reopen`
  );
};

export const deleteTimesheetLineRequest = (token: string, companyId: string, timesheetLineId: string) => {
  return axios.delete(`/admin/pay/company/${companyId}/timesheet_lines/${timesheetLineId}`);
};

export const createTimesheetLineRequest = (
  token: string,
  companyId: string,
  data: TimesheetLine,
  payrunId: string
) => {
  return axios.post(
    `/admin/pay/company/${companyId}/pay_run/${payrunId}/employee/${data.employee_id}/timesheet_lines`,
    data
  );
};

export const editTimesheetLineRequest = (
  token: string,
  companyId: string,
  timesheetLineId: string,
  data: TimesheetLine
) => {
  return axios.patch(`/admin/pay/company/${companyId}/timesheet_lines/${timesheetLineId}`, data);
};

export const lookupEmployeesRequest = ({ token, companyId, payScheduleId, substring }: LookupEmployees) => {
  const query = substring ? `?substring=${substring}&limit=20` : '?limit=20';
  return axios.get(`/admin/pay/company/${companyId}/pay_schedule/${payScheduleId}/employees${query}`);
};

export const markPayrunAsPaidRequest = ({
  token,
  companyId,
  payScheduleId,
  payrunId,
}: MarkAsPaidRequest) => {
  return axios.post(
    `/admin/pay/company/${companyId}/pay_schedules/${payScheduleId}/pay_runs/${payrunId}/mark_as_paid`
  );
};

export const markPayrunAsPaidForEmployeeRequest = ({
  token,
  companyId,
  payScheduleId,
  payrunId,
  employeeId,
}: MarkAsPaidRequest) => {
  return axios.post(
    `/admin/pay/companies/${companyId}/pay_schedules/${payScheduleId}/pay_runs/${payrunId}/employees/${employeeId}/mark_as_paid`
  );
};

/** 202: accepted, not delivered. Admin sends carry no outsourced block — Vyce sends on its own behalf. */
export const sendPayslipsByEmailRequest = ({ token, payrunLineId }: SendPayslipsRequest) => {
  return axios.post<PayslipsSendStarted>(`${URL}/admin/v1/pay/payslips/${payrunLineId}/send_email`);
};

// company_id is no longer a query param — the admin route takes the line and the Legend token.
export const sendPayslipsBySMSRequest = ({ token, payrunLineId }: SendPayslipsRequest) => {
  return axios.post<PayslipsSendStarted>(`${URL}/admin/v1/pay/payslips/${payrunLineId}/send_sms`);
};

export const getPayslipsPDFRequest = (id: string) => {
  return axios.post<PresignedFile>(`${URL}/admin/v1/pay/payslips/${id}/pdf`);
};

export const getBACSFormatsRequest = () => {
  return axios.get(`/admin/pay/bacs`);
};

export const downloadPayrunBACSRequest = (
  companyId: string,
  payrunId: string,
  data: { bacs_format: string; unpaid: boolean }
) => {
  return axios.post(`/admin/pay/company/${companyId}/pay_runs/${payrunId}/bacs`, data);
};

export const downloadPayrunsBACSRequest = (
  companyId: string,
  data: { bacs_format: string; unpaid: boolean; pay_runs: string[] }
) => {
  return axios.post(`/admin/pay/company/${companyId}/pay_runs/bacs`, data);
};

export const getCisReportsRequest = (payload: ReportPayload) => {
  return axios.post(`/hmrc/reports/cis300/search`, payload);
};

export const getUserCisRequest = (userId: string) => {
  return axios.post(`/hmrc/transactions/cis_tax_rates/users/${userId}`, {
    offset: 0,
    limit: 1,
    order_by: [
      {
        field_name: 'updated_at',
        desc: true,
      },
    ],
  });
};

export const downloadCISReportRequest = ({ reportId }: DownloadCISReportRequest) => {
  return axios.get(`/hmrc/reports/cis300/${reportId}/xlsx`, {
    headers: {
      accept: 'application/json',
    },
    responseType: 'blob',
  });
};

export const refreshCISReportRequest = ({ companyId, taxYear, taxMonth }: RefreshCISReportRequest) => {
  return axios.post(`/hmrc/reports/cis300/refresh`, {
    company_id: companyId,
    tax_year: taxYear,
    tax_month: taxMonth,
  });
};

export const sendCis300ToHMRCRequest = ({ companyId, taxMonth, taxYear }: SendCISReportRequest) => {
  return axios.post(`/hmrc/reports/cis300/send`, {
    company_id: companyId,
    tax_month: taxMonth,
    tax_year: taxYear,
  });
};

export const getNextPayPeriodRequest = (payScheduleId: string) => {
  return axios.get(`/admin/pay/pay_schedule/${payScheduleId}/next_pay_period`);
};

export const generateNextPayrunRequest = ({ payScheduleId, copyPrevious }: GenerateNextPayrun) => {
  const query = copyPrevious ? '?copy_employees_only=true' : '';
  return axios.post(`/admin/pay/pay_schedule/${payScheduleId}/generate_next_pay_run${query}`);
};

export const getPayScheduleRequest = (companyId: string, payScheduleId: string) => {
  return axios.get(`/admin/pay/company/${companyId}/pay_schedules/${payScheduleId}`);
};

export const getHMRCSettingsByCompanyRequest = (companyId: string) => {
  return axios.get<HMRCSetting>(`/admin/pay/company/${companyId}/hmrc_settings`);
};

export const updatePaySchedulesRequest = (companyId: string, data: PaySchedule) => {
  return axios.patch(`/admin/pay/company/${companyId}/pay_schedules/${data.uuid}`, data);
};

export const getEmployeeListRequest = ({ data }: ListEmployeeRequest) => {
  return axios.post(`/admin/pay/employees`, data);
};

export const lookupEmployeeListRequest = ({ data }: ListEmployeeRequest) => {
  return axios.post(`/admin/pay/employees/lookup`, data);
};

export const searchPayslipsRequest = ({ userId, data }: GetPayslipsRequest) => {
  return axios.post(`${URL}/admin/v1/pay/users/${userId}/payslips/search`, data);
};

/**
 * TODO(VYCE-DRC): still on the legacy route — the gateway spec ships a replacement only for
 * the company export (/public/v1/.../pay_runs/{id}/export) and no admin twin. Until one
 * exists this workbook carries neither the DRC VAT columns nor the unpaid half of each
 * amount, so a Legend export will disagree with the employer's. Confirm with backend.
 */
export const getPayrunAsFileRequest = (token: string, companyId: string, payrunId: string) => {
  return axios.post(
    `/admin/pay/companies/${companyId}/pay_runs/${payrunId}/export_to_xslx`,
    {},
    {
      headers: {
        accept: 'application/json',
      },
      responseType: 'blob',
    }
  );
};

export const getTimesheetAsFileRequest = (token: string, companyId: string, payrunId: string) => {
  return axios.post(
    `/admin/pay/companies/${companyId}/timesheets/${payrunId}/export_to_xslx`,
    {},
    {
      headers: {
        accept: 'application/json',
      },
      responseType: 'blob',
    }
  );
};

export const getEmployeeTimesheetsRequest = ({ userId, data }: GetEmployeeTimesheetsRequest) => {
  return axios.post(`/admin/pay/users/${userId}/timesheet_lines`, data);
};

export const checkEmployeeCISRequest = (employee_id: string) => {
  return axios.post(`/admin/pay/employees/${employee_id}/check_cis`);
};

export const checkEmployeeVATRequest = (employee_id: string, vat_number: string) => {
  return axios.post(`/admin/pay/employees/${employee_id}/check_vat`, { vat_number });
};

/** Read-only despite being a POST: workers across all companies that have a VAT number on their profile. */
export const getEmployeesVATRequest = ({
  payload,
  signal,
}: {
  payload: EmployeesVATRequestProps;
  signal?: AbortSignal;
}) => {
  return axios.post<PaginatedList<EmployeeVATDTO>>(`${URL}/admin/v1/pay/employees/vat`, payload, { signal });
};

export const refreshPayrunRequest = (
  token: string,
  companyId: string,
  payScheduleId: string,
  payrunId: string
) => {
  return axios.post(
    `/admin/pay/company/${companyId}/pay_schedules/${payScheduleId}/pay_runs/${payrunId}/refresh`
  );
};

export const deleteTimesheetRequest = (companyId: string, payrunId: string) => {
  return axios.delete(`/admin/pay/companies/${companyId}/timesheets/${payrunId}`);
};

export const getTimesheetMappingRequest = (token: string, companyId: string, file: File) => {
  const bodyFormData = new FormData();
  bodyFormData.append('file', file);
  return axios.post(`/admin/pay/timesheet_mappings`, bodyFormData);
};

export const getEmployeePaySchedulesRequest = (employeeId: string) => {
  return axios.post(`/admin/pay/employees/${employeeId}/pay_schedules/search`, {
    offset: 0,
    limit: 100,
  });
};

export const changeEmployeePaySchemeRequest = (
  payScheduleId: string,
  employeeId: string,
  payScheme: string
) => {
  return axios.post(`/admin/pay/pay_schedules/${payScheduleId}/employees/${employeeId}/pay_scheme`, {
    pay_scheme: payScheme,
  });
};

export const sendPayslipsToEmployeesBySMSRequest = ({ token, payrunId }: SendBulkPayStatements) => {
  return axios.post<PayslipsSendStarted>(`${URL}/admin/v1/pay/pay_runs/${payrunId}/payslips/send_by_sms`);
};

export const sendPayslipsToEmployeesByEmailRequest = ({ token, payrunId }: SendBulkPayStatements) => {
  return axios.post<PayslipsSendStarted>(`${URL}/admin/v1/pay/pay_runs/${payrunId}/payslips/send_by_email`);
};

export const downloadHealthAndSafetyPDFRequest = ({ userId }: DownloadHealthAndSafetyPDFRequest) => {
  return axios.post(
    `/admin/pay/agreements/users/${userId}/health_and_safety`,
    {},
    {
      responseType: 'blob',
    }
  );
};

export const getAgreementsRequest = ({
  token,
  substring,
  pay_scheme,
  limit = 1000,
  offset = 0,
  order_by,
  userId,
}: GetAgreementsRequest) => {
  return axios.post(`${URL}/admin/v1/pay/documents/users/${userId}`, {
    substring,
    pay_scheme,
    limit,
    offset,
    // The documents gateway expects the sort key under `field`; `field_name` is silently ignored.
    order_by: order_by?.map(({ field_name, desc }) => ({ field: field_name, desc })),
  });
};

export const downloadAgreementRequest = ({ token, payScheduleId, userId }: GetAgreementRequest) => {
  return axios.post(
    `/admin/pay/agreements/users/${userId}/pay_schedules/${payScheduleId}`,
    {},
    {
      responseType: 'blob',
    }
  );
};

// Regenerates the agreement document and returns a fresh { url, file_name }.
export const refreshAgreementRequest = ({ userId, payScheduleId }: RefreshPayrolleeDocRequest) => {
  return axios.post<RefreshedPayrolleeDoc>(
    `${URL}/admin/v1/pay/documents/users/${userId}/agreement/refresh`,
    {
      pay_schedule_id: payScheduleId,
    }
  );
};

// Regenerates the medical record document and returns a fresh { url, file_name }.
export const refreshMedicalRecordRequest = ({ userId, payScheduleId }: RefreshPayrolleeDocRequest) => {
  return axios.post<RefreshedPayrolleeDoc>(
    `${URL}/admin/v1/pay/documents/users/${userId}/medical_delivery/refresh`,
    {
      pay_schedule_id: payScheduleId,
    }
  );
};

const agreementVersionsUrl = (userId: string, payScheduleId: string) =>
  `${URL}/admin/v1/pay/documents/users/${userId}/pay_schedules/${payScheduleId}/versions`;

// Newest first; hidden versions are included with hidden_at/hidden_by/hidden_reason filled in.
export const getAgreementVersionsRequest = ({ userId, payScheduleId }: GetAgreementVersionsRequest) => {
  return axios.get<AgreementVersion[]>(agreementVersionsUrl(userId, payScheduleId));
};

// Reissues the agreement with an explicit business type and date. Changes only the document,
// not the worker's business type on their profile.
export const reissueAgreementRequest = ({
  userId,
  payScheduleId,
  businessType,
  agreementDate,
  note,
}: ReissueAgreementRequest) => {
  return axios.post<RefreshedPayrolleeDoc>(
    `${URL}/admin/v1/pay/documents/users/${userId}/agreement/versions`,
    {
      pay_schedule_id: payScheduleId,
      business_type: businessType,
      agreement_date: agreementDate,
      note,
    }
  );
};

export const updateAgreementVersionNoteRequest = ({
  userId,
  payScheduleId,
  version,
  note,
}: UpdateAgreementVersionNoteRequest) => {
  return axios.patch<AgreementVersion>(`${agreementVersionsUrl(userId, payScheduleId)}/${version}`, {
    note: note || null,
  });
};

export const makeAgreementVersionCurrentRequest = ({
  userId,
  payScheduleId,
  version,
  note,
}: MakeAgreementVersionCurrentRequest) => {
  // The endpoint 422s on a missing body — always send at least {}.
  return axios.post<AgreementVersion>(
    `${agreementVersionsUrl(userId, payScheduleId)}/${version}/make-current`,
    note ? { note } : {}
  );
};

// Withdraws (hides) a version; the row and the PDF are kept server-side.
export const hideAgreementVersionRequest = ({
  userId,
  payScheduleId,
  version,
  reason,
}: HideAgreementVersionRequest) => {
  // DELETE with a body — axios only sends it via config.data.
  return axios.delete<AgreementVersion>(`${agreementVersionsUrl(userId, payScheduleId)}/${version}`, {
    data: { reason },
  });
};

export const getCompanyBankAccountRequest = ({ companyId }: GetCompanyBankAccountRequest) => {
  return axios.get(`/admin/pay/companies/${companyId}/bank_account`);
};

export const createOrUpdateCompanyBankAccountRequest = ({
  companyId,
  bankAccount,
}: CreateOrUpdateCompanyBankAccountRequest) => {
  return axios.put(`/admin/pay/companies/${companyId}/bank_account`, bankAccount);
};

/** The filters decide the rows, so `limit` here is the export size, not a page. */
export const downloadPayslipsRequestByLegend = ({ userId, data }: GetPayslipsRequest) => {
  return axios.post<PresignedFile>(`${URL}/admin/v1/pay/users/${userId}/payslips/xlsx`, data);
};

export const getCis300SubmitsRequest = (payload: Cis300SubmitsRequest) => {
  return axios.post(`/hmrc/reports/cis300/submits/search`, payload);
};
